import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { db, filesCollection, paymentsCollection, messagesCollection, notificationsCollection, fileDataCollection, doc, getDocs, getDoc, setDoc, addDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy } from '../utils/firebase';
import { collection } from 'firebase/firestore';
export interface FileItem {
  id: string;
  name: string;
  type: 'book' | 'pastpaper' | 'other';
  coverUrl: string;
  description: string;
  price: number;
  createdAt: string;
  downloadUrl?: string;
  fileFormat: string;
  fileSize?: number;
  hasFileData?: boolean;
  subject?: string;
  level?: string;
  rating?: number;
  reviewCount?: number;
}
export interface PaymentRequest {
  id: string;
  userId: string;
  userName: string;
  userPhone: string;
  fileId: string;
  fileName: string;
  amount: number;
  payerMtnNumber: string;
  payerName: string;
  status: 'pending' | 'verified' | 'denied';
  createdAt: string;
  resolvedAt?: string;
  feedback?: string;
  discountCode?: string;
  originalAmount?: number;
}
export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderType: 'user' | 'admin';
  receiverId: string;
  message: string;
  attachment?: {
    name: string;
    url: string;
  };
  createdAt: string;
  conversationId: string;
}
export interface Notification {
  id: string;
  type: 'new_user' | 'payment_request' | 'new_message' | 'payment_verified' | 'payment_denied';
  message: string;
  data?: any;
  read: boolean;
  createdAt: string;
}
export interface Review {
  id: string;
  fileId: string;
  userId: string;
  userName: string;
  rating: number;
  comment: string;
  createdAt: string;
}
export interface DiscountCode {
  id: string;
  code: string;
  percentage: number;
  active: boolean;
  usageCount: number;
  maxUses: number;
  createdAt: string;
}
interface DataContextType {
  files: FileItem[];
  payments: PaymentRequest[];
  messages: ChatMessage[];
  notifications: Notification[];
  reviews: Review[];
  discountCodes: DiscountCode[];
  addFile: (file: Omit<FileItem, 'id' | 'createdAt'> & {
    id?: string;
  }) => Promise<string>;
  removeFile: (fileId: string) => void;
  storeFileData: (fileId: string, data: string) => void;
  getFileData: (fileId: string) => Promise<string | null>;
  addPaymentRequest: (req: Omit<PaymentRequest, 'id' | 'createdAt' | 'status'>) => void;
  verifyPayment: (paymentId: string) => void;
  denyPayment: (paymentId: string) => void;
  sendMessage: (msg: Omit<ChatMessage, 'id' | 'createdAt'>) => void;
  getConversationMessages: (conversationId: string) => ChatMessage[];
  getConversations: () => {
    userId: string;
    userName: string;
    lastMessage: string;
    unread: number;
  }[];
  markNotificationRead: (notifId: string) => void;
  markAllNotificationsRead: () => void;
  getUnreadNotificationCount: () => number;
  getPaymentStatus: (userId: string, fileId: string) => PaymentRequest | undefined;
  addReview: (review: Omit<Review, 'id' | 'createdAt'>) => Promise<void>;
  getFileReviews: (fileId: string) => Review[];
  addDiscountCode: (code: Omit<DiscountCode, 'id' | 'createdAt' | 'usageCount'>) => Promise<void>;
  validateDiscount: (code: string) => DiscountCode | null;
  useDiscountCode: (code: string) => Promise<void>;
  refreshData: () => void;
  isLoading: boolean;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const reviewsCollection = collection(db, 'reviews');
const discountCodesCollection = collection(db, 'discountCodes');
function safeGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
function safeSet(key: string, value: any): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}
export function DataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [files, setFiles] = useState<FileItem[]>(() => safeGet('edumarket_files', []));
  const [payments, setPayments] = useState<PaymentRequest[]>(() => safeGet('edumarket_payments', []));
  const [messages, setMessages] = useState<ChatMessage[]>(() => safeGet('edumarket_messages', []));
  const [notifications, setNotifications] = useState<Notification[]>(() => safeGet('edumarket_notifications', []));
  const [reviews, setReviews] = useState<Review[]>(() => safeGet('edumarket_reviews', []));
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>(() => safeGet('edumarket_discounts', []));
  const [isLoading, setIsLoading] = useState(true);
  // Real-time Firestore listeners
  useEffect(() => {
    setIsLoading(true);
    const unsubFiles = onSnapshot(query(filesCollection, orderBy('createdAt', 'asc')), snapshot => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as FileItem);
      setFiles(data);
      safeSet('edumarket_files', data);
    }, error => console.warn('Firestore files listener error:', error));
    const unsubPayments = onSnapshot(query(paymentsCollection, orderBy('createdAt', 'asc')), snapshot => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as PaymentRequest);
      setPayments(data);
      safeSet('edumarket_payments', data);
    }, error => console.warn('Firestore payments listener error:', error));
    const unsubMessages = onSnapshot(query(messagesCollection, orderBy('createdAt', 'asc')), snapshot => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as ChatMessage);
      setMessages(data);
      safeSet('edumarket_messages', data);
      setIsLoading(false);
    }, error => {
      console.warn('Firestore messages listener error:', error);
      setIsLoading(false);
    });
    const unsubNotifs = onSnapshot(query(notificationsCollection, orderBy('createdAt', 'asc')), snapshot => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Notification);
      setNotifications(data);
      safeSet('edumarket_notifications', data);
    }, error => console.warn('Firestore notifications listener error:', error));
    const unsubReviews = onSnapshot(query(reviewsCollection, orderBy('createdAt', 'asc')), snapshot => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as Review);
      setReviews(data);
      safeSet('edumarket_reviews', data);
    }, () => {});
    const unsubDiscounts = onSnapshot(query(discountCodesCollection, orderBy('createdAt', 'asc')), snapshot => {
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }) as DiscountCode);
      setDiscountCodes(data);
      safeSet('edumarket_discounts', data);
    }, () => {});
    const timer = setTimeout(() => setIsLoading(false), 3000);
    return () => {
      unsubFiles();
      unsubPayments();
      unsubMessages();
      unsubNotifs();
      unsubReviews();
      unsubDiscounts();
      clearTimeout(timer);
    };
  }, []);
  const addFile = useCallback(async (file: Omit<FileItem, 'id' | 'createdAt'> & {
    id?: string;
  }): Promise<string> => {
    const id = file.id || 'file_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    const newFile: FileItem = {
      ...file,
      id,
      createdAt: new Date().toISOString(),
      rating: 0,
      reviewCount: 0
    };
    try {
      await setDoc(doc(filesCollection, id), newFile);
    } catch (e) {
      console.warn('Firestore write failed:', e);
      setFiles(prev => {
        const next = [...prev, newFile];
        safeSet('edumarket_files', next);
        return next;
      });
    }
    return id;
  }, []);
  const removeFile = useCallback(async (fileId: string) => {
    try {
      await deleteDoc(doc(filesCollection, fileId));
      try {
        await deleteDoc(doc(fileDataCollection, fileId));
      } catch {}
    } catch (e) {
      console.warn('Firestore delete failed:', e);
      setFiles(prev => {
        const next = prev.filter(f => f.id !== fileId);
        safeSet('edumarket_files', next);
        return next;
      });
    }
    try {
      localStorage.removeItem(`edumarket_filedata_${fileId}`);
    } catch {}
  }, []);
  const storeFileData = useCallback(async (fileId: string, data: string) => {
    try {
      localStorage.setItem(`edumarket_filedata_${fileId}`, data);
    } catch {}
    if (data.length < 900000) {
      try {
        await setDoc(doc(fileDataCollection, fileId), {
          data,
          fileId
        });
      } catch (e) {
        console.warn('File data too large for Firestore:', e);
      }
    }
  }, []);
  const getFileData = useCallback(async (fileId: string): Promise<string | null> => {
    try {
      const local = localStorage.getItem(`edumarket_filedata_${fileId}`);
      if (local) return local;
    } catch {}
    try {
      const snap = await getDoc(doc(fileDataCollection, fileId));
      if (snap.exists()) {
        const data = snap.data().data as string;
        try {
          localStorage.setItem(`edumarket_filedata_${fileId}`, data);
        } catch {}
        return data;
      }
    } catch (e) {
      console.warn('Failed to get file data:', e);
    }
    return null;
  }, []);
  const addPaymentRequest = useCallback(async (req: Omit<PaymentRequest, 'id' | 'createdAt' | 'status'>) => {
    const id = 'pay_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5);
    const newReq: PaymentRequest = {
      ...req,
      id,
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    const notifId = 'notif_' + Date.now() + '_pay';
    const notif: Notification = {
      id: notifId,
      type: 'payment_request',
      message: `Payment request: ${req.payerName} (${req.payerMtnNumber}) - ${req.amount} FRW for "${req.fileName}"`,
      data: {
        paymentId: id,
        userId: req.userId
      },
      read: false,
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(paymentsCollection, id), newReq);
      await setDoc(doc(notificationsCollection, notifId), notif);
    } catch (e) {
      console.warn('Firestore write failed:', e);
      setPayments(prev => {
        const next = [...prev, newReq];
        safeSet('edumarket_payments', next);
        return next;
      });
      setNotifications(prev => {
        const next = [...prev, notif];
        safeSet('edumarket_notifications', next);
        return next;
      });
    }
  }, []);
  const verifyPayment = useCallback(async (paymentId: string) => {
    const payment = payments.find(p => p.id === paymentId);
    if (!payment) return;
    try {
      await updateDoc(doc(paymentsCollection, paymentId), {
        status: 'verified',
        resolvedAt: new Date().toISOString()
      });
      try {
        const userSnap = await getDoc(doc(db, 'users', payment.userId));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const purchasedFiles = userData.purchasedFiles || [];
          if (!purchasedFiles.includes(payment.fileId)) {
            purchasedFiles.push(payment.fileId);
            await updateDoc(doc(db, 'users', payment.userId), {
              purchasedFiles
            });
          }
        }
      } catch {}
      try {
        const currentRaw = localStorage.getItem('edumarket_current_user');
        if (currentRaw) {
          const current = JSON.parse(currentRaw);
          if (current.id === payment.userId && !current.purchasedFiles.includes(payment.fileId)) {
            current.purchasedFiles.push(payment.fileId);
            localStorage.setItem('edumarket_current_user', JSON.stringify(current));
          }
        }
      } catch {}
      const notifId = 'notif_' + Date.now() + '_ver';
      await setDoc(doc(notificationsCollection, notifId), {
        id: notifId,
        type: 'payment_verified',
        message: `Payment verified for "${payment.fileName}"! User can now download.`,
        data: {
          paymentId,
          userId: payment.userId,
          fileId: payment.fileId
        },
        read: false,
        createdAt: new Date().toISOString()
      });
    } catch (e) {
      console.warn('Firestore verify failed:', e);
      setPayments(prev => {
        const next = prev.map(p => p.id === paymentId ? {
          ...p,
          status: 'verified' as const,
          resolvedAt: new Date().toISOString()
        } : p);
        safeSet('edumarket_payments', next);
        return next;
      });
    }
  }, [payments]);
  const denyPayment = useCallback(async (paymentId: string) => {
    const payment = payments.find(p => p.id === paymentId);
    if (!payment) return;
    try {
      await updateDoc(doc(paymentsCollection, paymentId), {
        status: 'denied',
        resolvedAt: new Date().toISOString(),
        feedback: 'Not registered by admin. Make sure you paid the correct amount.'
      });
      const notifId = 'notif_' + Date.now() + '_den';
      await setDoc(doc(notificationsCollection, notifId), {
        id: notifId,
        type: 'payment_denied',
        message: `Payment denied for "${payment.fileName}". Make sure you paid the correct amount.`,
        data: {
          paymentId,
          userId: payment.userId
        },
        read: false,
        createdAt: new Date().toISOString()
      });
    } catch (e) {
      console.warn('Firestore deny failed:', e);
      setPayments(prev => {
        const next = prev.map(p => p.id === paymentId ? {
          ...p,
          status: 'denied' as const,
          resolvedAt: new Date().toISOString(),
          feedback: 'Not registered by admin.'
        } : p);
        safeSet('edumarket_payments', next);
        return next;
      });
    }
  }, [payments]);
  const sendMessage = useCallback(async (msg: Omit<ChatMessage, 'id' | 'createdAt'>) => {
    const id = 'msg_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5);
    const newMsg: ChatMessage = {
      ...msg,
      id,
      createdAt: new Date().toISOString()
    };
    const notifId = 'notif_' + Date.now() + '_msg';
    const notif: Notification = {
      id: notifId,
      type: 'new_message',
      message: `New message from ${msg.senderName}: ${msg.message.slice(0, 50)}${msg.message.length > 50 ? '...' : ''}`,
      data: {
        conversationId: msg.conversationId,
        senderId: msg.senderId
      },
      read: false,
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(messagesCollection, id), newMsg);
      await setDoc(doc(notificationsCollection, notifId), notif);
    } catch (e) {
      console.warn('Firestore message failed:', e);
      setMessages(prev => {
        const next = [...prev, newMsg];
        safeSet('edumarket_messages', next);
        return next;
      });
      setNotifications(prev => {
        const next = [...prev, notif];
        safeSet('edumarket_notifications', next);
        return next;
      });
    }
  }, []);
  const getConversationMessages = useCallback((conversationId: string) => {
    return messages.filter(m => m.conversationId === conversationId).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [messages]);
  const getConversations = useCallback(() => {
    const convMap = new Map<string, {
      userId: string;
      userName: string;
      lastMessage: string;
      unread: number;
      lastTime: string;
    }>();
    messages.forEach(m => {
      const userId = m.senderType === 'user' ? m.senderId : m.receiverId;
      const existing = convMap.get(m.conversationId);
      if (!existing || new Date(m.createdAt) > new Date(existing.lastTime)) {
        convMap.set(m.conversationId, {
          userId,
          userName: m.senderType === 'user' ? m.senderName : existing?.userName || userId,
          lastMessage: m.message,
          unread: (existing?.unread || 0) + (m.senderType === 'user' && !existing ? 1 : 0),
          lastTime: m.createdAt
        });
      }
    });
    return Array.from(convMap.values());
  }, [messages]);
  const markNotificationRead = useCallback(async (notifId: string) => {
    try {
      await updateDoc(doc(notificationsCollection, notifId), {
        read: true
      });
    } catch {
      setNotifications(prev => {
        const next = prev.map(n => n.id === notifId ? {
          ...n,
          read: true
        } : n);
        safeSet('edumarket_notifications', next);
        return next;
      });
    }
  }, []);
  const markAllNotificationsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.read);
    try {
      await Promise.all(unread.map(n => updateDoc(doc(notificationsCollection, n.id), {
        read: true
      })));
    } catch {
      setNotifications(prev => {
        const next = prev.map(n => ({
          ...n,
          read: true
        }));
        safeSet('edumarket_notifications', next);
        return next;
      });
    }
  }, [notifications]);
  const getUnreadNotificationCount = useCallback(() => notifications.filter(n => !n.read).length, [notifications]);
  const getPaymentStatus = useCallback((userId: string, fileId: string) => {
    return payments.find(p => p.userId === userId && p.fileId === fileId && p.status !== 'denied');
  }, [payments]);
  // === NEW: Reviews ===
  const addReview = useCallback(async (review: Omit<Review, 'id' | 'createdAt'>) => {
    const id = 'rev_' + Date.now() + '_' + Math.random().toString(36).slice(2, 5);
    const newReview: Review = {
      ...review,
      id,
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(reviewsCollection, id), newReview);
      // Update file rating
      const fileReviews = [...reviews.filter(r => r.fileId === review.fileId), newReview];
      const avgRating = fileReviews.reduce((sum, r) => sum + r.rating, 0) / fileReviews.length;
      await updateDoc(doc(filesCollection, review.fileId), {
        rating: Math.round(avgRating * 10) / 10,
        reviewCount: fileReviews.length
      });
    } catch (e) {
      console.warn('Review save failed:', e);
      setReviews(prev => {
        const next = [...prev, newReview];
        safeSet('edumarket_reviews', next);
        return next;
      });
    }
  }, [reviews]);
  const getFileReviews = useCallback((fileId: string) => {
    return reviews.filter(r => r.fileId === fileId).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [reviews]);
  // === NEW: Discount Codes ===
  const addDiscountCode = useCallback(async (code: Omit<DiscountCode, 'id' | 'createdAt' | 'usageCount'>) => {
    const id = 'disc_' + Date.now();
    const newCode: DiscountCode = {
      ...code,
      id,
      usageCount: 0,
      createdAt: new Date().toISOString()
    };
    try {
      await setDoc(doc(discountCodesCollection, id), newCode);
    } catch (e) {
      console.warn('Discount code save failed:', e);
      setDiscountCodes(prev => {
        const next = [...prev, newCode];
        safeSet('edumarket_discounts', next);
        return next;
      });
    }
  }, []);
  const validateDiscount = useCallback((code: string): DiscountCode | null => {
    const found = discountCodes.find(d => d.code.toLowerCase() === code.toLowerCase() && d.active && d.usageCount < d.maxUses);
    return found || null;
  }, [discountCodes]);
  const useDiscountCode = useCallback(async (code: string) => {
    const found = discountCodes.find(d => d.code.toLowerCase() === code.toLowerCase());
    if (!found) return;
    try {
      await updateDoc(doc(discountCodesCollection, found.id), {
        usageCount: found.usageCount + 1
      });
    } catch {
      setDiscountCodes(prev => {
        const next = prev.map(d => d.id === found.id ? {
          ...d,
          usageCount: d.usageCount + 1
        } : d);
        safeSet('edumarket_discounts', next);
        return next;
      });
    }
  }, [discountCodes]);
  const refreshData = useCallback(() => {}, []);
  return <DataContext.Provider value={{
    files,
    payments,
    messages,
    notifications,
    reviews,
    discountCodes,
    addFile,
    removeFile,
    storeFileData,
    getFileData,
    addPaymentRequest,
    verifyPayment,
    denyPayment,
    sendMessage,
    getConversationMessages,
    getConversations,
    markNotificationRead,
    markAllNotificationsRead,
    getUnreadNotificationCount,
    getPaymentStatus,
    addReview,
    getFileReviews,
    addDiscountCode,
    validateDiscount,
    useDiscountCode,
    refreshData,
    isLoading
  }} data-id="element-186">
      {children}
    </DataContext.Provider>;
}
export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}